import axios from 'axios';
import { redactText, isPrivacyGuardEnabled } from '../utils/redactionEngine';
import { stripInvisibleChars } from '../utils/textReadability';
import { logPrivacyEvent, maybeDailySnapshot } from '../utils/privacyGuardLog';

// API Base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Task B: real file names never leave the device; only the kind of file is sent.
export const anonFileName = (name) => {
  if (!name) return null;
  const ext = String(name).split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'document.pdf';
  if (['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'gif', 'bmp', 'tif', 'tiff'].includes(ext)) return 'image.jpg';
  return 'document.txt';
};

class APIService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api`,
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 30000 // 30 second timeout
    });
    this.sessionToken = null;
    
    // Add response interceptor for better error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        // Log network errors for debugging
        if (error.code === 'ECONNABORTED') {
          console.error('API Request timeout');
        } else if (!error.response) {
          console.error('API Network error - server may be down');
        } else if (error.response.status === 401) {
          // 401 = unauthorized - log it, let the calling code handle it
          console.warn('401 Unauthorized:', error.config?.url);
        } else if (error.response.status >= 500) {
          console.error('API Server error:', error.response.status);
        }
        return Promise.reject(error);
      }
    );
  }
  
  // Retry wrapper for critical operations
  async withRetry(operation, maxRetries = 2, delay = 1000) {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        const isLastAttempt = attempt === maxRetries;
        const isRetryable = !error.response || error.response.status >= 500 || error.code === 'ECONNABORTED';
        
        if (isLastAttempt || !isRetryable) {
          throw error;
        }
        
        console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Exponential backoff
      }
    }
  }
  
  // Health check - verify API is reachable
  async checkHealth() {
    try {
      const response = await this.api.get('/health', { timeout: 5000 });
      return { healthy: true, status: response.data };
    } catch (error) {
      return { healthy: false, error: error.message };
    }
  }

  /**
   * Send heartbeat to keep session alive during long operations.
   * Call this periodically during file uploads, OCR processing, etc.
   * 
   * Prevents session timeout while user is actively using the app.
   * 
   * @param {string} sessionToken - User's session token
   * @returns {Promise<Object>} Result { success: boolean }
   */
  async sendHeartbeat(sessionToken) {
    try {
      const response = await this.api.post('/session/heartbeat', null, {
        params: { session_token: sessionToken },
        timeout: 5000
      });
      return response.data;
    } catch (error) {
      console.warn('[APIService] Heartbeat failed:', error.message);
      return { success: false };
    }
  }
  
  async createSession() {
    try {
      const response = await this.api.post('/session/create');
      this.sessionToken = response.data.token;
      localStorage.setItem('health_portal_session', this.sessionToken);
      return response.data;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }
  
  async getOrCreateSession() {
    if (this.sessionToken) return this.sessionToken;
    
    const stored = localStorage.getItem('health_portal_session');
    if (stored) {
      this.sessionToken = stored;
      return stored;
    }
    
    const session = await this.createSession();
    return session.token;
  }
  
  /**
   * Self-Repair: Reset stale session and create a fresh anonymous session.
   * Called when a 401 indicates the current token is invalid/expired.
   */
  async _resetAndCreateSession() {
    console.log('[Self-Repair] Clearing stale session token and creating fresh session');
    this.sessionToken = null;
    localStorage.removeItem('health_portal_session');
    const session = await this.createSession();
    return session;
  }
  
  async queryHealth(query, context = null, viewpoint = null, source = null, options = {}) {
    // queryAlreadyRedacted: the query text has already passed the client-side
    // redaction review gate (user-reviewed) — don't re-redact it. Context is
    // still redacted here as a safety net for stored-record data.
    const redactQuery = (q) => options.queryAlreadyRedacted ? { redactedText: q } : redactText(q);
    // One id per question: links the answer, lettered answers and the rating in the Beta Q&A log
    const exchangeId = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    try {
      const token = await this.getOrCreateSession();
      // Phase 2: Privacy Guard — redact PII from query and context
      const redactedQuery = redactQuery(query);
      const redactedContext = context ? redactText(context) : { redactedText: null };
      maybeDailySnapshot();
      if (!isPrivacyGuardEnabled()) {
        logPrivacyEvent('sent_unprotected');
      } else if (!options.queryAlreadyRedacted) {
        const counts = Object.fromEntries((redactedQuery.tokens || []).map(t => [t.type, t.count]));
        logPrivacyEvent('question_redacted', { counts, total: redactedQuery.totalRedacted || 0, review_shown: false, review_edited: false });
      }
      const response = await this.api.post('/query', {
        session_token: token,
        query: redactedQuery.redactedText,
        context: redactedContext.redactedText,
        viewpoint: viewpoint,
        source: source,
        exchange_id: exchangeId
      });
      return { ...response.data, sent_query: redactedQuery.redactedText };
    } catch (error) {
      // Self-Repair: Session Fallback — auto-create new session and retry on 401
      if (error.response?.status === 401) {
        console.warn('[Self-Repair] 401 on queryHealth — creating new session and retrying');
        try {
          await this._resetAndCreateSession();
          const newToken = this.sessionToken;
          const redactedQuery = redactQuery(query);
          const redactedContext = context ? redactText(context) : { redactedText: null };
          const retryResponse = await this.api.post('/query', {
            session_token: newToken,
            query: redactedQuery.redactedText,
            context: redactedContext.redactedText,
            viewpoint: viewpoint,
            source: source,
            exchange_id: exchangeId
          });
          return { ...retryResponse.data, sent_query: redactedQuery.redactedText };
        } catch (retryError) {
          console.error('[Self-Repair] Retry after session reset also failed:', retryError);
          throw retryError;
        }
      }
      console.error('Health query failed:', error);
      throw error;
    }
  }
  
  async getUsageStats() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/usage/${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get usage stats:', error);
      return null;
    }
  }

  // ========== DOCUMENT ANALYSIS ==========

  // Phase A' (May 15, 2026): client OCR engine version sent with each
  // /analyze/document call for telemetry regression-spotting. Non-PII;
  // backward-compatible (optional string, capped at 64 chars).
  // Source: package.json "tesseract.js": "^6.0.1". Update this string
  // when tesseract.js version is bumped, or when an alternative OCR
  // engine is wired (e.g. 'vision-api@gpt-5.1' for the Vision path).
  // See TELEMETRY_DESIGN.md §2.2 (D4).
  static OCR_ENGINE_VERSION = 'tesseract.js@6.0.1';

  async analyzeDocument(documentText, documentType = null, fileName = null, options = {}) {
    // documentAlreadyRedacted: text has passed the client-side review gate — don't re-redact.
    // Pass 1 of the visible-content guard: invisible Unicode characters never leave the device.
    documentText = stripInvisibleChars(documentText);
    const redactDoc = (t) => options.documentAlreadyRedacted ? { redactedText: t } : redactText(t);
    try {
      const token = await this.getOrCreateSession();
      // Phase 2: Privacy Guard — redact PII from document text
      const redacted = redactDoc(documentText);
      const response = await this.api.post('/analyze/document', {
        session_token: token,
        document_text: redacted.redactedText,
        document_type: documentType,
        file_name: anonFileName(fileName),
        ocr_engine_version: APIService.OCR_ENGINE_VERSION
      }, { signal: options.signal });
      return response.data;
    } catch (error) {
      // Self-Repair: Session Fallback — auto-create new session and retry on 401
      if (error.response?.status === 401) {
        console.warn('[Self-Repair] 401 on analyzeDocument — creating new session and retrying');
        try {
          await this._resetAndCreateSession();
          const newToken = this.sessionToken;
          const redacted = redactDoc(documentText);
          const retryResponse = await this.api.post('/analyze/document', {
            session_token: newToken,
            document_text: redacted.redactedText,
            document_type: documentType,
            file_name: anonFileName(fileName),
            ocr_engine_version: APIService.OCR_ENGINE_VERSION
          }, { signal: options.signal });
          return retryResponse.data;
        } catch (retryError) {
          console.error('[Self-Repair] Retry after session reset also failed:', retryError);
          throw retryError;
        }
      }
      console.error('Document analysis failed:', error);
      throw error;
    }
  }

  // Report a client-side pipeline failure to the backend so it lands in the
  // Admin Issues queue. Used by the parallel upload pipeline (uploadPipeline.js)
  // when a doc fails to analyse on the client side (network, timeout, etc.).
  // Best-effort: never throws — a failure to report a failure shouldn't
  // cascade. Server enforces 30/min/IP cap.
  async reportPipelineFailure({ fileName, stage, errorMessage, detectedLanguage }) {
    try {
      const token = await this.getOrCreateSession();
      await this.api.post('/upload/report-failure', {
        session_token: token,
        file_name: anonFileName(fileName),
        stage: String(stage || 'unknown').slice(0, 40),
        error_message: errorMessage ? String(errorMessage).slice(0, 2000) : null,
        detected_language: detectedLanguage || null,
      });
    } catch (e) {
      console.warn('[reportPipelineFailure] best-effort report failed:', e?.message);
    }
  }

  // Report a successful per-record analysis duration to the backend so the
  // admin Pipeline Performance widget has data for p50/p90/p99. Best-effort.
  async reportPipelineSuccess({ fileName, startedAt, durationMs, detectedLanguage, confirmedLanguage }) {
    try {
      const token = await this.getOrCreateSession();
      await this.api.post('/upload/report-success', {
        session_token: token,
        file_name: anonFileName(fileName),
        started_at: startedAt || null,
        duration_ms: Math.max(0, Math.min(3_600_000, Math.round(durationMs || 0))),
        detected_language: detectedLanguage || null,
        confirmed_language: confirmedLanguage || null,
      });
    } catch (e) {
      console.warn('[reportPipelineSuccess] best-effort report failed:', e?.message);
    }
  }

  // Admin: aggregated pipeline performance for the dashboard widget.
  async getPipelineMetrics(days = 7) {
    const token = await this.getOrCreateSession();
    const response = await this.api.get('/admin/pipeline-metrics', {
      params: { session_token: token, days },
    });
    return response.data;
  }

  // Admin: read the active document-analysis redaction profile.
  // Returns { value: 'off'|'minimal'|'full', since, set_by }.
  async getRedactionProfile() {
    const token = await this.getOrCreateSession();
    const response = await this.api.get('/admin/redaction-profile', {
      params: { session_token: token },
    });
    return response.data;
  }

  // Admin: set the document-analysis redaction profile. Audited to
  // governance_events on every flip.
  async setRedactionProfile(value) {
    const token = await this.getOrCreateSession();
    const response = await this.api.post('/admin/redaction-profile', {
      session_token: token,
      value,
    });
    return response.data;
  }

  // See STEP_1_LLM_OPTIMIZATION.md § 2.4 (Conservative cleanup flag).
  // Admin: read / set the document-analysis cleanup profile.
  // Values: 'off' (default) | 'shadow' (audit only, LLM input unchanged) |
  // 'active' (audit + apply cleaned text to LLM input).
  // Rollback: flip back to 'off' (instantaneous, no deploy needed).
  async getCleanupProfile() {
    const token = await this.getOrCreateSession();
    const response = await this.api.get('/admin/cleanup-profile', {
      params: { session_token: token },
    });
    return response.data;
  }

  async setCleanupProfile(value) {
    const token = await this.getOrCreateSession();
    const response = await this.api.post('/admin/cleanup-profile', {
      session_token: token,
      value,
    });
    return response.data;
  }

  // Member: thumbs-up / thumbs-down on an analysis. Vote is upserted by
  // (email, record_id) so a member changing their mind replaces the prior
  // vote. Negative votes also fan out to member_issues for admin triage.
  async submitAnalysisFeedback({ recordId, vote, reason, confirmedLanguage }) {
    const token = await this.getOrCreateSession();
    const response = await this.api.post('/feedback/analysis', {
      session_token: token,
      record_id: String(recordId),
      vote,
      reason: reason ? String(reason).slice(0, 500) : null,
      confirmed_language: confirmedLanguage || null,
    });
    return response.data;
  }

  async analyzeGuestDocument(documentText, documentType = null) {    try {
      // Phase 2: Privacy Guard — redact PII from guest document text
      const redacted = redactText(documentText);
      const response = await this.api.post('/analyze/guest', {
        document_text: redacted.redactedText,
        document_type: documentType
      });
      return response.data;
    } catch (error) {
      console.error('Guest document analysis failed:', error);
      throw error;
    }
  }

  // ========== DEVICE TRACKING ==========

  /**
   * Collect device information for debugging and analytics
   * No cookies required - captures browser/device metadata only
   */
  getDeviceInfo() {
    const ua = navigator.userAgent;
    
    // Parse OS
    let os = 'Unknown';
    let osVersion = '';
    if (/iPhone|iPad|iPod/.test(ua)) {
      os = /iPad/.test(ua) ? 'iPadOS' : 'iOS';
      const match = ua.match(/OS (\d+[._]\d+)/);
      osVersion = match ? match[1].replace('_', '.') : '';
    } else if (/Android/.test(ua)) {
      os = 'Android';
      const match = ua.match(/Android (\d+\.?\d*)/);
      osVersion = match ? match[1] : '';
    } else if (/Windows/.test(ua)) {
      os = 'Windows';
      const match = ua.match(/Windows NT (\d+\.\d+)/);
      osVersion = match ? match[1] : '';
    } else if (/Mac OS X/.test(ua)) {
      os = 'macOS';
      const match = ua.match(/Mac OS X (\d+[._]\d+)/);
      osVersion = match ? match[1].replace('_', '.') : '';
    } else if (/Linux/.test(ua)) {
      os = 'Linux';
    }

    // Parse Browser
    let browser = 'Unknown';
    let browserVersion = '';
    if (/CriOS/.test(ua)) {
      browser = 'Chrome iOS';
      const match = ua.match(/CriOS\/(\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (/Chrome/.test(ua) && !/Chromium|Edge/.test(ua)) {
      browser = 'Chrome';
      const match = ua.match(/Chrome\/(\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
      browser = 'Safari';
      const match = ua.match(/Version\/(\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (/Firefox/.test(ua)) {
      browser = 'Firefox';
      const match = ua.match(/Firefox\/(\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (/Edge/.test(ua)) {
      browser = 'Edge';
      const match = ua.match(/Edge\/(\d+)/);
      browserVersion = match ? match[1] : '';
    }

    // Parse Device Type
    let deviceType = 'Desktop';
    if (/iPhone/.test(ua)) deviceType = 'iPhone';
    else if (/iPad/.test(ua)) deviceType = 'iPad';
    else if (/iPod/.test(ua)) deviceType = 'iPod';
    else if (/Android/.test(ua) && /Mobile/.test(ua)) deviceType = 'Android Phone';
    else if (/Android/.test(ua)) deviceType = 'Android Tablet';
    else if (/Mobile/.test(ua)) deviceType = 'Mobile';

    return {
      userAgent: ua,
      os,
      osVersion,
      browser,
      browserVersion,
      deviceType,
      screenWidth: window.screen?.width || 0,
      screenHeight: window.screen?.height || 0,
      viewportWidth: window.innerWidth || 0,
      viewportHeight: window.innerHeight || 0,
      devicePixelRatio: window.devicePixelRatio || 1,
      touchSupport: 'ontouchend' in document,
      language: navigator.language || '',
      platform: navigator.platform || '',
      cookiesEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Send device info to backend for storage
   * Called after successful login
   */
  async trackDevice(email) {
    try {
      const deviceInfo = this.getDeviceInfo();
      const response = await this.api.post('/user/device', {
        email,
        device_info: deviceInfo
      });
      return response.data;
    } catch (error) {
      // Don't fail login if device tracking fails
      console.warn('Device tracking failed:', error);
      return { success: false };
    }
  }

  /**
   * Analyze an image using Vision AI (GPT-4 Vision)
   * Use when standard OCR fails on medical device screens, digital displays, etc.
   * @param {string} imageBase64 - Base64 encoded image (with or without data URL prefix)
   * @param {string} context - Optional context hint (e.g., "blood gas analyzer", "vital monitor")
   */
  async analyzeImageWithVision(imageBase64, context = null, fileName = null) {
    try {
      console.log('apiService.analyzeImageWithVision called, image length:', imageBase64?.length);
      const token = localStorage.getItem('health_portal_session') || this.sessionToken;
      const response = await this.api.post('/analyze/vision', {
        image_base64: imageBase64,
        context: context,
        session_token: token || undefined,
        file_name: anonFileName(fileName) || undefined,
      }, {
        timeout: 60000 // Vision analysis can take longer
      });
      console.log('apiService.analyzeImageWithVision response:', {
        success: response.data?.success,
        hasText: !!response.data?.text,
        hasExtractedText: !!response.data?.extracted_text
      });
      return response.data;
    } catch (error) {
      console.error('Vision analysis failed:', error);
      throw error;
    }
  }

  /**
   * Process PDF server-side (for iOS Safari compatibility)
   * Extracts text and optionally renders to image for Vision AI
   * @param {string} pdfBase64 - Base64 encoded PDF file
   * @param {number} pageNumber - Page number to process (1-indexed)
   * @param {boolean} processAllPages - Whether to process all pages (default: false)
   */
  async processPDFServerSide(pdfBase64, pageNumber = 1, processAllPages = false, fileName = null) {
    try {
      console.log('apiService.processPDFServerSide called, pdf length:', pdfBase64?.length, 'processAllPages:', processAllPages);
      const token = localStorage.getItem('health_portal_session') || this.sessionToken;
      const response = await this.api.post('/analyze/pdf', {
        pdf_base64: pdfBase64,
        page_number: pageNumber,
        extract_text: true,
        render_image: true, // Always render image for Vision AI fallback on image-based PDFs
        process_all_pages: processAllPages,
        session_token: token || undefined,
        file_name: anonFileName(fileName) || undefined,
      }, {
        timeout: processAllPages ? 120000 : 30000 // 2 min for all pages, 30s for single page
      });
      console.log('apiService.processPDFServerSide response:', {
        success: response.data?.success,
        hasText: !!response.data?.text,
        textLength: response.data?.text?.length || 0,
        numPages: response.data?.num_pages,
        pagesProcessed: response.data?.pages_processed,
        hasImage: !!response.data?.image_base64
      });
      return response.data;
    } catch (error) {
      console.error('Server-side PDF processing failed:', error);
      throw error;
    }
  }

  // ========== AUTHENTICATION ==========

  async checkBetaMode() {
    try {
      const response = await this.api.get('/auth/beta-mode');
      return response.data;
    } catch (error) {
      console.error('Failed to check beta mode:', error);
      return { beta_mode_enabled: false };
    }
  }

  async register(email, password, betaCode = null) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/auth/register', {
        email,
        password,
        session_token: token,
        beta_code: betaCode
      });
      return response.data;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  }

  async login(email, password, forceLogout = false) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/auth/login', {
        email,
        password,
        session_token: token,
        force_logout: forceLogout
      });
      
      // Track device info after successful login
      if (response.data?.success || response.data?.token) {
        this.trackDevice(email).catch(() => {}); // Fire and forget
      }
      
      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      // Check for session conflict (409 status)
      if (error.response?.status === 409) {
        const detail = error.response?.data?.detail;
        if (detail && detail.error === 'session_conflict') {
          return {
            success: false,
            error: 'session_conflict',
            message: detail.message,
            hint: detail.hint,
            active_since: detail.active_since
          };
        }
      }
      throw error;
    }
  }

  async updateUserEmail(newEmail, currentPassword) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/auth/update-email', {}, {
        params: {
          session_token: token,
          new_email: newEmail,
          current_password: currentPassword
        }
      });
      return response.data;
    } catch (error) {
      console.error('Update email failed:', error);
      throw error;
    }
  }

  async updateUserPassword(currentPassword, newPassword) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/auth/update-password', {}, {
        params: {
          session_token: token,
          current_password: currentPassword,
          new_password: newPassword
        }
      });
      return response.data;
    } catch (error) {
      console.error('Update password failed:', error);
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/auth/me?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  // ========== PASSWORD RESET ==========

  async forgotPassword(email) {
    try {
      const response = await this.api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Forgot password failed:', error);
      throw error;
    }
  }

  async verifyResetToken(token) {
    try {
      const response = await this.api.get(`/auth/verify-reset-token/${token}`);
      return response.data;
    } catch (error) {
      console.error('Verify reset token failed:', error);
      throw error;
    }
  }

  async resetPassword(token, newPassword) {
    try {
      const response = await this.api.post('/auth/reset-password', {
        token,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Reset password failed:', error);
      throw error;
    }
  }

  // ========== GUEST ANALYSIS ==========

  async analyzeGuestDocument(documentText) {
    try {
      const redacted = redactText(documentText);
      const response = await this.api.post('/analyze/guest', {
        document_text: redacted.redactedText
      });
      return response.data;
    } catch (error) {
      console.error('Guest analysis failed:', error);
      throw error;
    }
  }

  // ========== EMAIL VERIFICATION ==========

  async sendVerificationEmail() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/auth/send-verification?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Send verification failed:', error);
      throw error;
    }
  }

  async verifyEmail(verificationToken) {
    try {
      const response = await this.api.post('/auth/verify-email', {
        token: verificationToken
      });
      return response.data;
    } catch (error) {
      console.error('Verify email failed:', error);
      throw error;
    }
  }

  async getVerificationStatus() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/auth/verification-status?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Get verification status failed:', error);
      throw error;
    }
  }

  // ========== DELETE ACCOUNT ==========

  async deleteAccount(password, confirmation) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/auth/delete-account', {
        password,
        confirmation
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Delete account failed:', error);
      throw error;
    }
  }

  // ========== SESSION MANAGEMENT ==========

  async getSessionStatus() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/auth/session-status?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Get session status failed:', error);
      // CRITICAL FIX: Don't auto-invalidate session on network errors!
      // Mobile networks are flaky - a timeout doesn't mean session is invalid
      // Return "unknown" state instead of forcing logout
      return { 
        valid: true,  // Assume valid on network error - don't logout user
        expired: false, 
        network_error: true,
        message: 'Could not verify session - network issue'
      };
    }
  }

  async refreshSession() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/auth/refresh-session?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Refresh session failed:', error);
      throw error;
    }
  }

  async logoutSession() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/auth/logout?session_token=${token}`);
      this.sessionToken = null;
      localStorage.removeItem('health_portal_session');
      return response.data;
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear local session
      this.sessionToken = null;
      localStorage.removeItem('health_portal_session');
      return { success: true };
    }
  }

  // ========== ADMIN ENDPOINTS ==========

  async getAllUsers() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/users?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get users:', error);
      throw error;
    }
  }

  async promoteUser(userId, newRole) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/users/promote', {
        user_id: userId,
        new_role: newRole
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to promote user:', error);
      throw error;
    }
  }

  async deactivateUser(userId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/users/${userId}/deactivate`, {}, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      throw error;
    }
  }

  async activateUser(userId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/users/${userId}/activate`, {}, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to activate user:', error);
      throw error;
    }
  }

  async deleteUser(userId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.delete(`/admin/users/${userId}`, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  }

  async cleanupNonMembers() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/users/cleanup-non-members?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to cleanup non-members:', error);
      throw error;
    }
  }

  async syncPaidMembers() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/payments/sync-paid-members?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to sync paid members:', error);
      throw error;
    }
  }

  async reclassifyNonPayerChurns() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/reclassify-non-payer-churns?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to reclassify non-payer churns:', error);
      throw error;
    }
  }




  async linkFamilyMember(primaryUserId, memberEmail) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/family/link', {
        primary_user_id: primaryUserId,
        member_email: memberEmail
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to link family member:', error);
      throw error;
    }
  }

  async unlinkFamilyMember(userId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/family/unlink/${userId}`, {}, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to unlink family member:', error);
      throw error;
    }
  }

  async getFamilyMembers(familyGroupId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/family/${familyGroupId}`, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get family members:', error);
      throw error;
    }
  }

  async activateSubscription(userEmail, planType, billingCycle, durationMonths = 12, isComplimentary = false) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/subscription/activate', {
        user_email: userEmail,
        plan_type: planType,
        billing_cycle: billingCycle,
        duration_months: durationMonths,
        is_complimentary: isComplimentary
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to activate subscription:', error);
      throw error;
    }
  }

  async deactivateSubscription(userId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/subscription/deactivate/${userId}`, {}, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to deactivate subscription:', error);
      throw error;
    }
  }

  async getAdminStats() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/stats?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get admin stats:', error);
      throw error;
    }
  }

  // ========== SESSION THRESHOLD SETTINGS ==========

  async getSessionThreshold() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/settings/session-threshold?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get session threshold:', error);
      throw error;
    }
  }

  async setSessionThreshold(thresholdSeconds) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/settings/session-threshold?session_token=${token}&threshold_seconds=${thresholdSeconds}`);
      return response.data;
    } catch (error) {
      console.error('Failed to set session threshold:', error);
      throw error;
    }
  }

  // ========== BETA CODE MANAGEMENT ==========

  async generateBetaCode(maxUses, expiresDays = null, description = null, customCode = null, accessDuration = null, label = null) {
    try {
      const token = await this.getOrCreateSession();
      const payload = {
        max_uses: maxUses,
        expires_days: expiresDays,
        description: description
      };
      if (customCode) payload.custom_code = customCode;
      if (accessDuration) payload.access_duration = accessDuration;
      if (label) payload.label = label;
      const response = await this.api.post('/admin/beta-codes/generate', payload, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to generate beta code:', error);
      throw error;
    }
  }

  async getBetaCodes() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/beta-codes?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get beta codes:', error);
      throw error;
    }
  }

  async revokeBetaCode(codeId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/beta-codes/${codeId}/revoke`, {}, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to revoke beta code:', error);
      throw error;
    }
  }

  async deleteBetaCode(codeId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.delete(`/admin/beta-codes/${codeId}`, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to delete beta code:', error);
      throw error;
    }
  }

  // ========== EXPERIMENT MANAGEMENT ==========

  async createExperiment(name, description, groups, trafficSplit, useGeoStratification) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/experiments/create', {
        name,
        description,
        groups,
        traffic_split: trafficSplit,
        use_geo_stratification: useGeoStratification
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create experiment:', error);
      throw error;
    }
  }

  async listExperiments() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/experiments?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to list experiments:', error);
      throw error;
    }
  }

  async activateExperiment(experimentId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/experiments/${experimentId}/activate`, {}, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to activate experiment:', error);
      throw error;
    }
  }

  async pauseExperiment(experimentId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/experiments/${experimentId}/pause`, {}, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to pause experiment:', error);
      throw error;
    }
  }

  async createVariant(name, provider, modelName, temperature, systemPrompt, customApiKey, useCustomKey, description) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/variants/create', {
        name,
        provider,
        model_name: modelName,
        temperature,
        system_prompt_override: systemPrompt,
        custom_api_key: customApiKey,
        use_custom_key: useCustomKey,
        description
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create variant:', error);
      throw error;
    }
  }

  async listVariants() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/variants?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to list variants:', error);
      throw error;
    }
  }

  async createMapping(experimentId, groupId, variantId, functionName) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/mappings/create', {
        experiment_id: experimentId,
        group_id: groupId,
        variant_id: variantId,
        function_name: functionName
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create mapping:', error);
      throw error;
    }
  }

  async getExperimentMappings(experimentId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/experiments/${experimentId}/mappings?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get mappings:', error);
      throw error;
    }
  }

  async getExperimentResults(experimentId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/experiments/${experimentId}/results?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get experiment results:', error);
      throw error;
    }
  }

  async recordMetric(userId, metricType, value, metadata) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/metrics/record', {
        user_id: userId,
        metric_type: metricType,
        value,
        metadata
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to record metric:', error);
      throw error;
    }
  }

  // ========== STRIPE PAYMENTS ==========

  async getPaymentPackages() {
    try {
      const response = await this.api.get('/payments/packages');
      return response.data;
    } catch (error) {
      console.error('Failed to get payment packages:', error);
      throw error;
    }
  }

  async createCheckoutSession(packageId, userEmail = null) {
    try {
      const token = await this.getOrCreateSession();
      const originUrl = window.location.origin;
      const response = await this.api.post('/payments/checkout', {
        package_id: packageId,
        origin_url: originUrl,
        user_email: userEmail
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      throw error;
    }
  }

  async getPaymentStatus(sessionId) {
    try {
      const response = await this.api.get(`/payments/status/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get payment status:', error);
      throw error;
    }
  }

  async getUserSubscription() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/user/subscription?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get subscription:', error);
      throw error;
    }
  }

  async getAdminPayments(limit = 50) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/payments?session_token=${token}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get admin payments:', error);
      throw error;
    }
  }

  // ========== PROSPECTS & EMAIL SEQUENCE ==========

  async getAdminProspects(group = 'all') {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/prospects?session_token=${token}&group=${group}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get prospects:', error);
      throw error;
    }
  }

  async getEmailSequence(group = 'exit_intent') {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/email-sequence?session_token=${token}&group=${group}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get email sequence:', error);
      throw error;
    }
  }

  async updateEmailSequence(sequence, group = 'exit_intent') {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.put(`/admin/email-sequence?session_token=${token}`, { sequence, group });
      return response.data;
    } catch (error) {
      console.error('Failed to update email sequence:', error);
      throw error;
    }
  }

  async sendProspectStep(email, step, group = 'exit_intent') {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/prospects/${encodeURIComponent(email)}/send-step?session_token=${token}`, { step, group });
      return response.data;
    } catch (error) {
      console.error('Failed to send prospect step:', error);
      throw error;
    }
  }

  async deleteProspect(email, group = 'exit_intent') {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.delete(`/admin/prospects/${encodeURIComponent(email)}?session_token=${token}&group=${group}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete prospect:', error);
      throw error;
    }
  }

  async updateLeadStatus(group, email, status) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.patch(`/admin/leads/${group}/${encodeURIComponent(email)}/status?session_token=${token}`, { status });
      return response.data;
    } catch (error) {
      console.error('Failed to update lead status:', error);
      throw error;
    }
  }

  // ========== ADMIN MONGO DATA VIEWER (P0) ==========
  // Backed by /api/admin/data-viewer/* endpoints — see SAR_USER_DATA_DISCLOSURE_SPEC.md
  // Every call writes an entry to db.admin_data_access_log per CODING_PROTOCOLS §7.

  async getDataViewerCollections() {
    const token = await this.getOrCreateSession();
    const response = await this.api.get(`/admin/data-viewer/collections?session_token=${token}`);
    return response.data;
  }

  async getDataViewerByUser(email) {
    const token = await this.getOrCreateSession();
    const response = await this.api.get(`/admin/data-viewer/by-user/${encodeURIComponent(email)}?session_token=${token}`);
    return response.data;
  }

  async getDataViewerCollection(name, page = 1, q = '', sortBy = '_id', sortDir = 'desc') {
    const token = await this.getOrCreateSession();
    const response = await this.api.get(
      `/admin/data-viewer/collection/${encodeURIComponent(name)}?session_token=${token}&page=${page}&q=${encodeURIComponent(q)}&sort_by=${encodeURIComponent(sortBy)}&sort_dir=${encodeURIComponent(sortDir)}`
    );
    return response.data;
  }

  async exportUserSAR(email) {
    // Returns a Blob — caller pipes to a download link.
    const token = await this.getOrCreateSession();
    const response = await this.api.post(
      `/admin/data-viewer/export/user/${encodeURIComponent(email)}?session_token=${token}`,
      {},
      { responseType: 'blob' }
    );
    return response.data;
  }

  async exportCollectionCSV(name, page = 1, q = '') {
    const token = await this.getOrCreateSession();
    const response = await this.api.post(
      `/admin/data-viewer/export/collection-csv/${encodeURIComponent(name)}?session_token=${token}&page=${page}&q=${encodeURIComponent(q)}`,
      {},
      { responseType: 'blob' }
    );
    return response.data;
  }

  // ========== SAR REQUEST WORKFLOW ==========
  // GDPR Article 15. User submits → admin reviews → admin authorizes → email fires.
  // Email is NEVER sent automatically — only when admin clicks "Authorize & Send".

  async sarUserSubmit() {
    const token = await this.getOrCreateSession();
    const response = await this.api.post('/sar/request', { session_token: token });
    return response.data;
  }

  async sarMyStatus() {
    const token = await this.getOrCreateSession();
    const response = await this.api.get(`/sar/my-status?session_token=${token}`);
    return response.data;
  }

  async sarAdminList() {
    const token = await this.getOrCreateSession();
    const response = await this.api.get(`/admin/sar-requests?session_token=${token}`);
    return response.data;
  }

  async sarAdminOpenCount() {
    const token = await this.getOrCreateSession();
    const response = await this.api.get(`/admin/sar-requests/open-count?session_token=${token}`);
    return response.data;
  }

  async sarAdminUpdateStatus(sarId, status, opts = {}) {
    const token = await this.getOrCreateSession();
    const body = { status, ...opts };
    const response = await this.api.patch(
      `/admin/sar-requests/${encodeURIComponent(sarId)}/status?session_token=${token}`,
      body
    );
    return response.data;
  }

  async sarAdminAuthorizeSend(sarId) {
    const token = await this.getOrCreateSession();
    const response = await this.api.post(
      `/admin/sar-requests/${encodeURIComponent(sarId)}/authorize-send?session_token=${token}`
    );
    return response.data;
  }

  async checkAbandonedTransactions() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/check-abandoned-transactions?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to check abandoned transactions:', error);
      throw error;
    }
  }

  async getSchedulerStatus() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/scheduler-status?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get scheduler status:', error);
      throw error;
    }
  }

  async getWelcomeExperience() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/welcome-experience?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get welcome experience:', error);
      throw error;
    }
  }


  async getProspectJourneys() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/prospect-journeys?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get prospect journeys:', error);
      throw error;
    }
  }

  async getEmailLog() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/email-log?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get email log:', error);
      throw error;
    }
  }

  async getEmailStats() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/email-stats?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get email stats:', error);
      throw error;
    }
  }

  // ========== GLOBAL LLM SETTINGS ==========

  async getLLMProviders() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/llm/providers?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get LLM providers:', error);
      throw error;
    }
  }

  async getGlobalLLM() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/llm/global?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get global LLM setting:', error);
      throw error;
    }
  }

  async setGlobalLLM(provider, model = null) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/llm/global', {
        provider,
        model
      }, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to set global LLM:', error);
      throw error;
    }
  }

  // ========== AI ICON SETTINGS ==========

  async getAiIconSettings() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/ai-icon?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get AI icon settings:', error);
      throw error;
    }
  }

  async setAiIconSettings(settings) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/ai-icon', settings, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save AI icon settings:', error);
      throw error;
    }
  }

  async getSiteSettings() {
    try {
      const response = await this.api.get('/site-settings');
      return response.data;
    } catch (error) {
      console.error('Failed to get site settings:', error);
      return null;
    }
  }

  // ========== DASHBOARD SETTINGS ==========

  async getDashboardSettings() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/dashboard-settings?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get dashboard settings:', error);
      throw error;
    }
  }

  async setDashboardSettings(settings) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/dashboard-settings', settings, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save dashboard settings:', error);
      throw error;
    }
  }

  // ========== VIEWPOINT SETTINGS ==========

  async getViewpointSettings() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/viewpoint-settings?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get viewpoint settings:', error);
      throw error;
    }
  }

  async setViewpointSettings(settings) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/viewpoint-settings', settings, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save viewpoint settings:', error);
      throw error;
    }
  }

  // ========== COST TRACKING ==========

  async getCostSettings() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/cost-settings?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get cost settings:', error);
      throw error;
    }
  }

  async setCostSettings(settings) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/cost-settings', settings, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save cost settings:', error);
      throw error;
    }
  }

  // ========== LLM USAGE DASHBOARD ==========

  async getLLMUsageStats(days = 7) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/llm-usage?session_token=${token}&days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get LLM usage stats:', error);
      throw error;
    }
  }

  // ========== TIMELINE HEALTH GOALS ==========

  async submitHealthGoal(goalText, email = null, visionEmotion = null) {
    try {
      const token = await this.getOrCreateSession();
      const payload = {
        goal_text: goalText,
        email: email,
        session_token: token
      };
      if (visionEmotion) payload.vision_emotion = visionEmotion;
      const response = await this.api.post('/timeline/health-goal', payload);
      return response.data;
    } catch (error) {
      console.error('Failed to submit health goal:', error);
      throw error;
    }
  }

  async getUserHealthGoal() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/timeline/health-goal?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get user health goal:', error);
      return { has_goal: false, goal_text: '', saved_at: null };
    }
  }

  async getTimelineSettings() {
    try {
      const response = await this.api.get('/timeline/settings');
      return response.data;
    } catch (error) {
      console.error('Failed to get timeline settings:', error);
      // Return defaults on error
      return {
        question_text: "Q. What do you want to learn from your health data?",
        popup_text: "This answer will be sent to admin along with your email address to inform us of your current health goals.",
        tagline_text: "Probably the best Private Health Timeline in the world. Built by you for your future."
      };
    }
  }

  async getAdminTimelineSettings() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/timeline/settings?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get admin timeline settings:', error);
      throw error;
    }
  }

  async saveAdminTimelineSettings(settings) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/timeline/settings', settings, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save timeline settings:', error);
      throw error;
    }
  }

  async getTimelineHealthGoals(status = 'all') {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/timeline/health-goals?session_token=${token}&status=${status}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get health goals:', error);
      throw error;
    }
  }

  async updateHealthGoalStatus(goalId, status) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/timeline/health-goals/${goalId}/status?session_token=${token}&status=${status}`);
      return response.data;
    } catch (error) {
      console.error('Failed to update health goal status:', error);
      throw error;
    }
  }

  // ========== FAQ MANAGER ==========

  async getAdminFAQs(status = 'all') {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/faqs?session_token=${token}&status=${status}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get FAQs:', error);
      throw error;
    }
  }

  async createFAQ(faq) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/faqs', faq, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create FAQ:', error);
      throw error;
    }
  }

  async updateFAQ(faqId, faq) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.put(`/admin/faqs/${faqId}`, faq, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update FAQ:', error);
      throw error;
    }
  }

  async updateFAQStatus(faqId, status) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.patch(`/admin/faqs/${faqId}/status?session_token=${token}&status=${status}`);
      return response.data;
    } catch (error) {
      console.error('Failed to update FAQ status:', error);
      throw error;
    }
  }

  async deleteFAQ(faqId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.delete(`/admin/faqs/${faqId}?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      throw error;
    }
  }

  async importDefaultFAQs() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/faqs/import-default?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to import default FAQs:', error);
      throw error;
    }
  }

  async getPublicFAQs() {
    try {
      const response = await this.api.get('/faqs/public');
      return response.data;
    } catch (error) {
      console.error('Failed to get public FAQs:', error);
      return { faqs: [], grouped: {} };
    }
  }

  // ========== GOOGLE REVIEW REQUESTS ==========

  async getReviewSettings() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/review-settings?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get review settings:', error);
      return { settings: {} };
    }
  }

  async saveReviewSettings(settings) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/review-settings', settings, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save review settings:', error);
      throw error;
    }
  }

  async getReviewRequests() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/review-requests?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get review requests:', error);
      return [];
    }
  }

  async markReviewRequestSent(userId, userEmail) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/review-requests?user_id=${encodeURIComponent(userId)}&user_email=${encodeURIComponent(userEmail)}&session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to mark review request as sent:', error);
      throw error;
    }
  }

  // ========== CONTENT MODERATION ==========

  async getModerationSettings() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/moderation/settings?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get moderation settings:', error);
      throw error;
    }
  }

  async updateModerationSettings(settings) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post('/admin/moderation/settings', settings, {
        params: { session_token: token }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update moderation settings:', error);
      throw error;
    }
  }

  async addBlockedKeyword(keyword) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/moderation/add-blocked-keyword?keyword=${encodeURIComponent(keyword)}&session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to add blocked keyword:', error);
      throw error;
    }
  }

  async removeBlockedKeyword(keyword) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/moderation/remove-blocked-keyword?keyword=${encodeURIComponent(keyword)}&session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to remove blocked keyword:', error);
      throw error;
    }
  }

  async addHealthKeyword(keyword) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/moderation/add-health-keyword?keyword=${encodeURIComponent(keyword)}&session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to add health keyword:', error);
      throw error;
    }
  }

  async removeHealthKeyword(keyword) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/moderation/remove-health-keyword?keyword=${encodeURIComponent(keyword)}&session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to remove health keyword:', error);
      throw error;
    }
  }

  async reportWronglyBlocked(question) {
    const token = await this.getOrCreateSession();
    await this.api.post('/moderation/wrongly-blocked', { session_token: token, question: redactText(question).redactedText.slice(0, 500) });
  }

  async getRejectedQueries(limit = 100) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/moderation/rejected-queries?session_token=${token}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get rejected queries:', error);
      throw error;
    }
  }

  async clearRejectedLogs() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.delete(`/admin/moderation/clear-rejected-logs?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to clear rejected logs:', error);
      throw error;
    }
  }

  async testModeration(query) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/moderation/test?query=${encodeURIComponent(query)}&session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to test moderation:', error);
      throw error;
    }
  }

  async getPrivacyGuardMember(email) {
    const token = await this.getOrCreateSession();
    const response = await this.api.get(`/admin/privacy-guard/member/${encodeURIComponent(email)}?session_token=${token}`);
    return response.data;
  }

  async getPrivacyGuardStates() {
    const token = await this.getOrCreateSession();
    const response = await this.api.get(`/admin/privacy-guard/states?session_token=${token}`);
    return response.data;
  }

  async getModerationTrapCheck() {
    const token = await this.getOrCreateSession();
    const response = await this.api.get(`/admin/moderation/trap-check?session_token=${token}`);
    return response.data;
  }

  // Pricing Configuration Methods
  async getPricingConfig() {
    try {
      const response = await this.api.get('/pricing/config');
      return response.data;
    } catch (error) {
      console.error('Failed to get pricing config:', error);
      throw error;
    }
  }

  async setPricingConfig(config) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/pricing/config?session_token=${token}`, config);
      return response.data;
    } catch (error) {
      console.error('Failed to save pricing config:', error);
      throw error;
    }
  }

  // PageSpeed Insights — Site Health Check
  async getPageSpeedReport(strategy = 'mobile', url = 'https://the.aidoctorben.com/') {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/pagespeed?session_token=${token}&strategy=${strategy}&url=${encodeURIComponent(url)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get PageSpeed report:', error);
      throw error;
    }
  }


  // General Site Settings Methods (persisted to database)
  // Used by: AboutPage.js, PricingPage.js, and other pages to fetch admin-configured content
  async getGeneralSettings(asAdmin = false) {
    try {
      // Public endpoint; admin token adds admin-only fields (beta code, beta email)
      const params = asAdmin ? { session_token: await this.getOrCreateSession() } : undefined;
      const response = await this.api.get('/admin/settings/general', { params });
      // Return full response object {settings: {...}} so callers can access response.settings
      return response.data;
    } catch (error) {
      console.error('[apiService] Failed to get general settings:', error);
      // Return empty settings object on error - callers will use defaults
      return { settings: {} };
    }
  }

  async saveGeneralSettings(settings) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/settings/general?session_token=${token}`, settings);
      return response.data;
    } catch (error) {
      console.error('Failed to save general settings:', error);
      throw error;
    }
  }

  // SEO Snippets
  async getSeoSnippets() {
    try {
      const response = await this.api.get('/admin/seo-snippets');
      return response.data;
    } catch (error) {
      console.error('Failed to get SEO snippets:', error);
      return { snippets: {} };
    }
  }

  async saveSeoSnippets(pages) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/seo-snippets?session_token=${token}`, { pages });
      return response.data;
    } catch (error) {
      console.error('Failed to save SEO snippets:', error);
      throw error;
    }
  }

  // Block Config (Admin Customise block ordering and labels)
  async getBlockConfig() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/block-config?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to load block config:', error);
      return { block_order: [], block_labels: {} };
    }
  }

  async saveBlockConfig(block_order, block_labels) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/block-config?session_token=${token}`, { block_order, block_labels });
      return response.data;
    } catch (error) {
      console.error('Failed to save block config:', error);
    }
  }

  // Auth Debug Logs (Super Admin)
  async getAuthLogs(filterType = 'all', lines = 100) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/auth-logs?session_token=${token}&filter_type=${filterType}&lines=${lines}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get auth logs:', error);
      return { success: false, error: error.message, logs: [] };
    }
  }

  // Referral Configuration Methods
  async getReferralConfig() {
    try {
      const response = await this.api.get('/referral/config');
      return response.data;
    } catch (error) {
      console.error('Failed to get referral config:', error);
      return {};
    }
  }

  async setReferralConfig(config) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/referral/config?session_token=${token}`, config);
      return response.data;
    } catch (error) {
      console.error('Failed to save referral config:', error);
      throw error;
    }
  }

  // Corporate Contact Configuration  
  async getCorporateConfig() {
    try {
      const response = await this.api.get('/corporate/contact-config');
      return response.data;
    } catch (error) {
      console.error('Failed to get corporate config:', error);
      throw error;
    }
  }

  async setCorporateConfig(config) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/corporate/contact-config?session_token=${token}`, config);
      return response.data;
    } catch (error) {
      console.error('Failed to save corporate config:', error);
      throw error;
    }
  }

  // FAQ Configuration  
  async getFAQConfig() {
    try {
      const response = await this.api.get('/faq/config');
      return response.data;
    } catch (error) {
      console.error('Failed to get FAQ config:', error);
      throw error;
    }
  }

  async setFAQConfig(config) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/faq/config?session_token=${token}`, config);
      return response.data;
    } catch (error) {
      console.error('Failed to save FAQ config:', error);
      throw error;
    }
  }

  // Privacy Consent Configuration
  async getPrivacyConsentConfig() {
    try {
      const response = await this.api.get('/privacy-consent/config');
      return response.data;
    } catch (error) {
      console.error('Failed to get privacy consent config:', error);
      throw error;
    }
  }

  async setPrivacyConsentConfig(config) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/privacy-consent/config?session_token=${token}`, config);
      return response.data;
    } catch (error) {
      console.error('Failed to save privacy consent config:', error);
      throw error;
    }
  }

  // Conversation Context Configuration
  async getConversationContextConfig() {
    try {
      const response = await this.api.get('/conversation-context/config');
      return response.data;
    } catch (error) {
      console.error('Failed to get conversation context config:', error);
      // Return default config on error
      return { enabled: true, admin_can_disable: true };
    }
  }

  async setConversationContextConfig(config) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/conversation-context/config?session_token=${token}`, config);
      return response.data;
    } catch (error) {
      console.error('Failed to save conversation context config:', error);
      throw error;
    }
  }

  // Subscription Status
  async getSubscriptionStatus() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/user/subscription?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      return { is_active: false, subscription_status: 'none' };
    }
  }

  // UI Layout Analytics
  async trackLayoutSelection(layout, userEmail = null) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/analytics/layout-selection?session_token=${token}&layout=${layout}${userEmail ? `&user_email=${encodeURIComponent(userEmail)}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Failed to track layout selection:', error);
    }
  }

  // Perspective Analytics (Added Jan 25, 2026)
  async trackPerspectiveSelection(perspective, userEmail = null) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/analytics/perspective-selection?session_token=${token}&perspective=${perspective}${userEmail ? `&user_email=${encodeURIComponent(userEmail)}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Failed to track perspective selection:', error);
    }
  }

  // User Demographics (Added Jan 26, 2026)
  async saveUserDemographics(demographics) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/user/demographics?session_token=${token}`, demographics);
      return response.data;
    } catch (error) {
      console.error('Failed to save demographics:', error);
      throw error;
    }
  }

  async getUserDemographics() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/user/demographics?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get demographics:', error);
      return null;
    }
  }

  async getDemographicsAnalytics() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/analytics/demographics?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get demographics analytics:', error);
      throw error;
    }
  }

  async getPersonaInjectionAnalytics() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/analytics/persona-injection?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get persona injection analytics:', error);
      throw error;
    }
  }

  // Body Profile (Added Jan 26, 2026)
  async saveBodyProfile(profile) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/user/body-profile?session_token=${token}`, profile);
      return response.data;
    } catch (error) {
      console.error('Failed to save body profile:', error);
      throw error;
    }
  }

  async getBodyProfile() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/user/body-profile?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get body profile:', error);
      return null;
    }
  }

  async checkBodyProfileComplete() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/user/body-profile/complete?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to check body profile completion:', error);
      return { complete: false, missing_fields: [], fields_completed: 0, fields_total: 9 };
    }
  }

  // ===== Setup Wizard =====
  async getSetupStatus() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/user/setup-status?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get setup status:', error);
      return { complete: true, steps: {} }; // Fail open for existing users
    }
  }

  async savePreferredName(name) {
    const token = await this.getOrCreateSession();
    const response = await this.api.post(`/user/preferred-name?session_token=${token}`, { preferred_name: name });
    return response.data;
  }

  async savePreferredViewpoint(viewpoint) {
    const token = await this.getOrCreateSession();
    const response = await this.api.post(`/user/preferred-viewpoint?session_token=${token}`, { viewpoint });
    return response.data;
  }

  async completeSetupWizard() {
    const token = await this.getOrCreateSession();
    const response = await this.api.post(`/user/complete-setup-wizard?session_token=${token}`);
    return response.data;
  }

  async markPrivacyReviewed() {
    const token = await this.getOrCreateSession();
    const response = await this.api.post(`/user/mark-privacy-reviewed?session_token=${token}`);
    return response.data;
  }

  async saveVisionEmotion(emotion) {
    const token = await this.getOrCreateSession();
    const response = await this.api.post(`/user/save-vision-emotion?session_token=${token}`, { emotion });
    return response.data;
  }

  async markConfettiSeen() {
    const token = await this.getOrCreateSession();
    const response = await this.api.post(`/user/mark-confetti-seen?session_token=${token}`);
    return response.data;
  }




  async getBodyProfileAnalytics() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/analytics/body-profiles?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get body profile analytics:', error);
      throw error;
    }
  }

  // Feature Usage Tracking (Added Jan 26, 2026)
  async trackFeatureUsage(eventType, eventData = {}) {
    try {
      const token = await this.getOrCreateSession();
      await this.api.post(`/analytics/feature-usage?session_token=${token}`, {
        event_type: eventType,
        event_data: eventData
      });
    } catch (error) {
      // Silently fail - don't break UX for analytics
      console.log('Feature tracking failed:', error);
    }
  }

  async getFeatureUsageAnalytics() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/analytics/feature-usage?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get feature usage analytics:', error);
      throw error;
    }
  }

  // Generic waitlist join (Added Jan 25, 2026)
  async joinWaitlist(waitlistType, userEmail) {
    try {
      const response = await this.api.post(`/${waitlistType}/waitlist`, { email: userEmail });
      return response.data;
    } catch (error) {
      console.error(`Failed to join ${waitlistType} waitlist:`, error);
      throw error;
    }
  }

  async getLayoutAnalytics() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/analytics/layouts?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get layout analytics:', error);
      throw error;
    }
  }

  async getPerspectiveAnalytics() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/analytics/perspectives?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get perspective analytics:', error);
      throw error;
    }
  }

  // Longevity Waitlist
  async joinLongevityWaitlist(data) {
    try {
      const response = await this.api.post('/longevity/waitlist', data);
      return response.data;
    } catch (error) {
      console.error('Failed to join longevity waitlist:', error);
      throw error;
    }
  }

  async getLongevityWaitlist() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/longevity/waitlist?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get longevity waitlist:', error);
      throw error;
    }
  }

  // Contrarian Waitlist
  async joinContrarianWaitlist(data) {
    try {
      const response = await this.api.post('/contrarian/waitlist', data);
      return response.data;
    } catch (error) {
      console.error('Failed to join contrarian waitlist:', error);
      throw error;
    }
  }

  async getContrarianWaitlist() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/contrarian/waitlist?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get contrarian waitlist:', error);
      throw error;
    }
  }

  // Body Profile Waitlist
  async joinBodyProfileWaitlist(data) {
    try {
      const response = await this.api.post('/body-profile/waitlist', data);
      return response.data;
    } catch (error) {
      console.error('Failed to join body profile waitlist:', error);
      throw error;
    }
  }

  async getBodyProfileWaitlist() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/body-profile/waitlist?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get body profile waitlist:', error);
      throw error;
    }
  }

  // ============================================================================
  // AUTOMATIC BACKUP EMAIL
  // ============================================================================
  // 
  // Design Philosophy (Apple-Inspired):
  // - Proactive protection: Backups just happen
  // - Invisible excellence: Users never think about this
  // - Trust through action: System just works
  //
  // @see /app/AUTOMATIC_BACKUP_ARCHITECTURE.md for full design
  // ============================================================================

  /**
   * Send backup data to user's email via Mailgun
   * 
   * This sends the complete backup JSON as an email attachment.
   * We do NOT store the backup on our servers - only the user has it.
   * 
   * @param {Object} backupData - Complete backup object with metadata
   * @param {Object} backupData.backup_metadata - Chain metadata
   * @param {Object} backupData.backup_stats - Stats about backup contents
   * @param {Object} backupData.data - The actual backup data
   * @param {string} sessionToken - User's session token
   * @returns {Promise<Object>} Result { success: boolean, message: string }
   */
  async sendBackupEmail(backupData, sessionToken) {
    try {
      console.log('[APIService] Sending backup email...', {
        sequence: backupData.backup_metadata?.backup_sequence,
        records: backupData.backup_stats?.total_records,
        isFull: backupData.backup_metadata?.is_full_backup
      });

      const response = await this.api.post(
        '/backup/send-email',
        backupData,
        {
          params: { session_token: sessionToken },
          timeout: 180000 // 3 minute timeout for full backup with images
        }
      );

      console.log('[APIService] Backup email response:', response.data);
      return response.data;
    } catch (error) {
      console.error('[APIService] Failed to send backup email:', error);
      // Return failure but don't throw - backup email failure shouldn't block user
      return { 
        success: false, 
        message: error.response?.data?.detail || error.message 
      };
    }
  }

  // ============================================================================
  // CHURNED USERS / RETENTION (Added Jan 27, 2026)
  // ============================================================================

  /**
   * Get list of deleted/churned users (Super Admin only)
   */
  async getDeletedUsers() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/deleted-users?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get deleted users:', error);
      throw error;
    }
  }

  /**
   * Update retention status for a churned user
   */
  async updateChurnedUserStatus(userId, status) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.patch(
        `/admin/deleted-users/${userId}/status?session_token=${token}`,
        { status }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update churned user status:', error);
      throw error;
    }
  }

  /**
   * Add a note to a churned user record
   */
  async addChurnedUserNote(userId, note) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(
        `/admin/deleted-users/${userId}/note?session_token=${token}`,
        { note }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to add churned user note:', error);
      throw error;
    }
  }

  /**
   * Send win-back email to a churned user
   */
  async sendWinBackEmail(userId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(
        `/admin/deleted-users/${userId}/send-email?session_token=${token}`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to send win-back email:', error);
      throw error;
    }
  }

  async enrollInWinbackSequence(userId) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(
        `/admin/winback/enroll/${userId}?session_token=${token}`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to enroll in win-back sequence:', error);
      throw error;
    }
  }

  async enrollInWinbackByEmail(email, firstName = '') {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(
        `/admin/winback/enroll-by-email?session_token=${token}`,
        { email, first_name: firstName }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to enroll by email in win-back sequence:', error);
      throw error;
    }
  }

  async getWinbackProspects() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/winback/prospects?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get win-back prospects:', error);
      throw error;
    }
  }

  async stripeEmailBackfill() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/payments/stripe-backfill?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to run Stripe email backfill:', error);
      throw error;
    }
  }

  async stripeFullSync() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/payments/stripe-full-sync?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to run full Stripe sync:', error);
      throw error;
    }
  }

  async bulkDeleteNoEmailTransactions() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/payments/bulk/clean-no-email?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to clean no-email transactions:', error);
      throw error;
    }
  }

  // ============ BETA ACCESS ============

  /**
   * Verify a beta access code
   */
  async verifyBetaCode(code) {
    try {
      const response = await this.api.post('/beta/verify-code', { code });
      return response.data;
    } catch (error) {
      console.error('Failed to verify beta code:', error);
      return { valid: false, message: 'Failed to verify code' };
    }
  }

  /**
   * Request beta access - sends delayed email with code
   */
  async requestBetaAccess(email, name = '') {
    try {
      const response = await this.api.post('/beta/request-access', { email, name });
      return response.data;
    } catch (error) {
      console.error('Failed to request beta access:', error);
      return { success: false, message: 'Failed to submit request' };
    }
  }

  /**
   * Get all beta access requests (admin)
   */
  async getBetaAccessRequests() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/beta-access-requests?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get beta access requests:', error);
      return [];
    }
  }

  /**
   * Get beta analytics data (admin)
   */
  async getBetaAnalytics() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/beta-analytics?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get beta analytics:', error);
      return null;
    }
  }

  // ============ PUBLIC WAITLIST (Feb 6, 2026) ============

  /**
   * Join the public waitlist
   */
  async joinWaitlist(email, referralCode = null) {
    try {
      const payload = { email };
      if (referralCode) {
        payload.referral_code = referralCode;
      }
      const response = await this.api.post('/waitlist/join', payload);
      return response.data;
    } catch (error) {
      console.error('Failed to join waitlist:', error);
      if (error.response?.data) {
        return error.response.data;
      }
      return { success: false, error: 'network_error', message: 'Failed to join waitlist' };
    }
  }

  /**
   * Get waitlist status for an email
   */
  async getWaitlistStatus(email) {
    try {
      const response = await this.api.get(`/waitlist/status?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get waitlist status:', error);
      throw error;
    }
  }

  /**
   * Get public waitlist stats (for landing page counter)
   */
  async getWaitlistStats() {
    try {
      const response = await this.api.get('/waitlist/stats');
      return response.data;
    } catch (error) {
      console.error('Failed to get waitlist stats:', error);
      return { total_signups: 0 };
    }
  }

  /**
   * Resend waitlist referral link
   */
  async resendWaitlistLink(email) {
    try {
      const response = await this.api.post('/waitlist/resend-link', { email });
      return response.data;
    } catch (error) {
      console.error('Failed to resend waitlist link:', error);
      throw error;
    }
  }

  /**
   * Get waitlist entries (admin)
   */
  async getAdminWaitlist(status = null, limit = 100, skip = 0) {
    try {
      const token = await this.getOrCreateSession();
      let url = `/admin/waitlist?session_token=${token}&limit=${limit}&skip=${skip}`;
      if (status) {
        url += `&status=${status}`;
      }
      const response = await this.api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to get admin waitlist:', error);
      return { entries: [], total: 0 };
    }
  }

  /**
   * Get waitlist leaderboard (admin)
   */
  async getWaitlistLeaderboard(limit = 10) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/waitlist/leaderboard?session_token=${token}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get waitlist leaderboard:', error);
      return { top_referrers: [] };
    }
  }

  /**
   * Send batch invites to waitlist (admin)
   */
  async sendWaitlistInviteBatch(count = 50) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.post(`/admin/waitlist/invite-batch?session_token=${token}&count=${count}`);
      return response.data;
    } catch (error) {
      console.error('Failed to send waitlist invites:', error);
      throw error;
    }
  }
  // ========== RETENTION ANALYTICS ==========

  async getRetentionTotals() {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/retention-totals?session_token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get retention totals:', error);
      throw error;
    }
  }

  // ========== AGENT ACTION LOG ==========

  async logAgentAction(actionType, targetEmail, targetUserId, details) {
    const token = await this.getOrCreateSession();
    const response = await this.api.post(`/admin/log-action?session_token=${token}`, {
      action_type: actionType,
      target_email: targetEmail,
      target_user_id: targetUserId,
      details
    });
    return response.data;
  }

  async getActionLog(limit = 50) {
    try {
      const token = await this.getOrCreateSession();
      const response = await this.api.get(`/admin/action-log?session_token=${token}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get action log:', error);
      throw error;
    }
  }
}

export default new APIService();
